import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-cream">
            <div className="hidden md:flex md:w-1/2 bg-[#1C1917] text-cream items-end p-12 relative overflow-hidden">
                <img src="https://images.unsplash.com/photo-1611021061285-16c871740efa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHx3b29kd29ya2luZyUyMGNyYWZ0c21hbnxlbnwwfHx8fDE3ODExNzc0OTV8MA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                <div className="relative">
                    <div className="text-xs uppercase tracking-[0.25em] text-walnut-light mb-3">Jassi Wood Works</div>
                    <h1 className="font-serif text-5xl leading-[1.05]">Admin Console</h1>
                    <p className="text-stone-300 mt-4 max-w-sm text-sm">Sign in to manage projects, content, inquiries and website settings.</p>
                </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
                <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md">
                    <div className="text-xs uppercase tracking-[0.25em] text-walnut-light mb-3">Sign in</div>
                    <h2 className="font-serif text-4xl text-ink">Welcome back.</h2>
                    <p className="text-stone-muted text-sm mt-2">Use your admin email and password.</p>

                    {error && (
                        <div data-testid="admin-login-error" className="mt-6 bg-red-50 border border-red-200 text-red-700 text-sm p-3">{error}</div>
                    )}

                    <div className="mt-8 space-y-5">
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest text-stone-muted block mb-2">Email</span>
                            <input
                                data-testid="admin-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-white border border-stone-border px-4 py-3 focus:outline-none focus:border-walnut"
                                placeholder="admin@jassiwoodworks.com"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest text-stone-muted block mb-2">Password</span>
                            <input
                                data-testid="admin-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-white border border-stone-border px-4 py-3 focus:outline-none focus:border-walnut"
                            />
                        </label>
                        <button
                            data-testid="admin-login-submit"
                            disabled={loading}
                            className="w-full bg-walnut text-cream py-3 text-sm tracking-wider uppercase hover:bg-walnut-dark disabled:opacity-60 inline-flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />} Sign in
                        </button>
                        <div className="flex justify-between text-xs">
                            <Link to="/admin/forgot-password" data-testid="forgot-password-link" className="text-walnut hover:text-walnut-light">Forgot password?</Link>
                            <Link to="/" className="text-stone-muted hover:text-walnut">Back to site</Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
